import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * StructuredData Component
 * Renders JSON-LD schema.org markup for SEO
 */
const StructuredData = ({ 
  type = 'WebSite',
  data = {}
}) => {
  const getSchema = () => {
    const baseUrl = 'https://safir-foundation.org';
    
    switch (type) {
      case 'EducationalOrganization':
        return {
          '@context': 'https://schema.org',
          '@type': 'EducationalOrganization',
          name: 'Safir Elite',
          alternateName: 'Safir the Elite',
          url: baseUrl,
          logo: `${baseUrl}/logo.jpg`,
          description: 'Afghanistan\'s leading educational platform offering expert-led courses, events, and academic programs.',
          address: {
            '@type': 'PostalAddress',
            addressCountry: 'AF',
            addressLocality: 'Kabul',
          },
          sameAs: [
            // Add social media URLs when available
          ],
          ...data
        };

      case 'Course':
        return {
          '@context': 'https://schema.org',
          '@type': 'Course',
          name: data.title || 'Course',
          description: data.description || '',
          provider: {
            '@type': 'EducationalOrganization',
            name: 'Safir Elite',
            sameAs: baseUrl
          },
          ...data
        };

      case 'Event':
        return {
          '@context': 'https://schema.org',
          '@type': 'Event',
          name: data.title || 'Event',
          description: data.description || '',
          startDate: data.date || '',
          location: {
            '@type': 'Place',
            name: data.location || 'Safir Elite',
            address: {
              '@type': 'PostalAddress',
              addressCountry: 'AF',
              addressLocality: 'Kabul'
            }
          },
          organizer: {
            '@type': 'EducationalOrganization',
            name: 'Safir Elite',
            url: baseUrl
          },
          ...data
        };

      case 'Scholarship':
        return {
          '@context': 'https://schema.org',
          '@type': 'EducationalOccupationalProgram',
          name: data.title || 'Scholarship',
          description: data.description || '',
          provider: { '@type': 'Organization', name: data.provider || 'Safir Foundation', url: baseUrl },
          timeToComplete: data.deadline || undefined,
          url: data.url || baseUrl + '/scholarships',
          ...data
        };

      case 'Article':
        return {
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: data.title || 'Article',
          description: data.description || '',
          image: data.image || `${baseUrl}/logo.jpg`,
          datePublished: data.publishedAt || new Date().toISOString(),
          dateModified: data.updatedAt || data.publishedAt || new Date().toISOString(),
          author: {
            '@type': 'Person',
            name: data.author || 'Safir Elite'
          },
          publisher: {
            '@type': 'EducationalOrganization',
            name: 'Safir Elite',
            logo: {
              '@type': 'ImageObject',
              url: `${baseUrl}/logo.jpg`
            }
          },
          ...data
        };

      case 'BreadcrumbList':
        return {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: (data.items || []).map((item, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: item.name,
            item: item.url ? `${baseUrl}${item.url}` : undefined
          }))
        };

      case 'WebSite':
      default:
        return {
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: 'Safir Elite',
          url: baseUrl,
          potentialAction: {
            '@type': 'SearchAction',
            target: {
              '@type': 'EntryPoint',
              urlTemplate: `${baseUrl}/courses?search={search_term_string}`
            },
            'query-input': 'required name=search_term_string'
          },
          ...data
        };
    }
  };

  const schema = getSchema();
  
  if (!schema) return null;

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

export default StructuredData;
